import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {InfiniteScrollModule} from 'ngx-infinite-scroll';
import {ContatosService} from '../../../services/contatos.service';
import {ConstantsService} from '../../ConstantsService';
import {moeda, segmentoCrm, SegmentoCrm} from '../crm-segmentos';
import {CartaoContatoCrmComponent} from '../cartao-contato-crm/cartao-contato-crm.component';
import {FaixaSegmentosCrmComponent, ResumoSegmentoCrm} from '../faixa-segmentos-crm/faixa-segmentos-crm.component';
import {RegrasCrmComponent, RegrasCrmTela} from '../regras-crm/regras-crm.component';
import {MensagemMiaCrmComponent} from '../mensagem-mia-crm/mensagem-mia-crm.component';

const TEXTO_DA_ORDEM: {[ordem: string]: string} = {
  ATRASO: 'quem mais passou do próprio ritmo primeiro',
  VALOR: 'quem mais gastou em 90 dias primeiro',
  RECENTE: 'pedido mais recente primeiro',
  NOME: 'ordem alfabética'
};

/** Fila de trabalho do CRM: um segmento por vez, na ordem em que vale a pena chamar. */
@Component({
  selector: 'app-fila-contatos',
  standalone: true,
  imports: [CommonModule, FormsModule, InfiniteScrollModule, CartaoContatoCrmComponent, FaixaSegmentosCrmComponent, RegrasCrmComponent, MensagemMiaCrmComponent],
  templateUrl: './fila-contatos.component.html',
  styleUrls: ['./fila-contatos.component.scss']
})
export class FilaContatosComponent implements OnInit, OnDestroy {
  segmento: SegmentoCrm = segmentoCrm('ESFRIANDO');
  resumo: ResumoSegmentoCrm[] = [];
  regras: RegrasCrmTela = null;
  regrasEditadasEm: string = null;
  defaultCalculadoEm: string = null;
  regrasAbertas = false;
  contatoMia: any = null;
  miaAberta = false;

  contatos: any[] = [];
  termoBusca = '';
  carregando = false;
  carregandoMais = false;
  fimDaLista = false;
  criandoCampanha = false;
  mensagem = '';
  erro = '';
  readonly tamanhoPagina = 20;

  private empresa: any;
  private assinaturas: Subscription[] = [];
  private requisicao = 0;

  constructor(private contatosService: ContatosService,
              private constantsService: ConstantsService, private router: Router, private rota: ActivatedRoute) {}

  ngOnInit(): void {
    this.assinaturas.push(this.rota.queryParamMap.subscribe(params => {
      const codigo = params.get('segmento');
      if (codigo && codigo !== this.segmento.codigo) {
        this.segmento = segmentoCrm(codigo);
        if (this.empresa) this.carregueContatos();
      }
    }));
    this.assinaturas.push(this.constantsService.empresa$.subscribe(empresa => {
      if (empresa && (!this.empresa || this.empresa.id !== empresa.id)) {
        this.empresa = empresa;
        this.carregueResumo();
        this.carregueContatos();
      }
    }));
  }

  ngOnDestroy(): void {
    this.assinaturas.forEach(a => a.unsubscribe());
  }

  get linhaDoSegmento(): ResumoSegmentoCrm {
    return this.resumo.find(l => l.status === this.segmento.codigo) || {status: this.segmento.codigo, qtde: 0, qtdeRecebeMensagem: 0, gasto90d: 0, pedidos90d: 0};
  }

  get textoOrdem(): string {
    return TEXTO_DA_ORDEM[this.segmento.ordem];
  }

  get gastoDoSegmento(): string {
    return moeda(this.linhaDoSegmento.gasto90d);
  }

  numero(valor: number): string {
    return (Number(valor) || 0).toLocaleString('pt-BR');
  }

  selecioneSegmento(codigo: string): void {
    this.router.navigate([], {relativeTo: this.rota, queryParams: {segmento: codigo}, replaceUrl: true});
  }

  carregueResumo(): void {
    this.contatosService.obtenhaResumoCrm().then((dados: any) => {
      this.resumo = dados.segmentos || [];
      this.regras = dados.regras;
      this.regrasEditadasEm = dados.regrasEditadasEm;
      this.defaultCalculadoEm = dados.defaultCalculadoEm;
    }).catch(() => {});
  }

  carregueContatos(): void {
    const requisicao = ++this.requisicao;
    this.carregando = true;
    this.fimDaLista = false;
    this.contatos = [];
    this.erro = '';
    this.contatosService.listeContatos(0, this.tamanhoPagina, this.parametros()).then((contatos: any[]) => {
      if (requisicao !== this.requisicao) return;
      this.contatos = contatos || [];
      this.fimDaLista = this.contatos.length < this.tamanhoPagina;
    }).catch(() => {
      if (requisicao === this.requisicao) this.erro = 'Não foi possível carregar os clientes deste segmento.';
    }).finally(() => {
      if (requisicao === this.requisicao) this.carregando = false;
    });
  }

  carregueMais(): void {
    if (this.carregando || this.carregandoMais || this.fimDaLista) return;
    const requisicao = this.requisicao;
    this.carregandoMais = true;
    this.contatosService.listeContatos(this.contatos.length, this.tamanhoPagina, this.parametros()).then((contatos: any[]) => {
      if (requisicao !== this.requisicao) return;
      this.contatos = [...this.contatos, ...(contatos || [])];
      this.fimDaLista = (contatos || []).length < this.tamanhoPagina;
    }).finally(() => this.carregandoMais = false);
  }

  busque(termo: string): void {
    this.termoBusca = termo;
    this.carregueContatos();
  }

  private parametros(): any {
    const params: any = {st: this.segmento.codigo, acionavel: 1, ordemCrm: this.segmento.ordem};
    if (this.termoBusca && this.termoBusca.trim()) params.q = this.termoBusca.trim();
    return params;
  }

  /** A tela de campanha monta o público do segmento e já sugere o texto. */
  crieCampanha(): void {
    this.criandoCampanha = true;
    this.router.navigate(['/admin/nova-campanha'], {queryParams: {segmento: this.segmento.codigo}})
      .finally(() => this.criandoCampanha = false);
  }

  abraMia(contato: any): void {
    this.contatoMia = contato;
    this.miaAberta = true;
  }

  /** CSV da fila atual (segmento e busca). */
  exporte(): void {
    const link = document.createElement('a');
    link.href = this.contatosService.urlExportacaoCrm(this.parametros());
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  verContato(contato: any): void {
    let link = '/admin/contatos';
    if (contato.telefone) {
      link = `${link}/${contato.codigoPais}${contato.telefone}`;
    } else {
      const documento = contato.cpf || contato.cnpj;
      if (documento) link = `${link}/documento/${documento}`;
    }
    this.router.navigate([link], {state: {contato}});
  }

  abraQuadro(): void {
    this.router.navigate(['/admin/contatos-kanban']);
  }

  salvouRegras(regras: RegrasCrmTela & {recalculando: boolean}): void {
    this.regras = regras;
    this.regrasEditadasEm = new Date().toISOString();
    this.regrasAbertas = false;
    this.mensagem = regras.recalculando
      ? 'Regras salvas. Os clientes estão sendo reclassificados; a fila atualiza em alguns minutos.'
      : 'Regras salvas. Já havia um recálculo em andamento; quando ele terminar, use "Recalcular agora" para aplicar as novas regras.';
  }

  recalculou(): void {
    this.regrasAbertas = false;
    this.mensagem = 'Recalculando todos os clientes. A fila atualiza em alguns minutos.';
  }

  trackPorId(_: number, contato: any): any {
    return contato.id;
  }
}
